import { useCallback, useEffect, useState } from 'react';
import Table, { TableProps } from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import Link from '@cloudscape-design/components/link';
import Multiselect, { MultiselectProps } from '@cloudscape-design/components/multiselect';
import Pagination from '@cloudscape-design/components/pagination';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import TextFilter from '@cloudscape-design/components/text-filter';
import { SchedulingThread, getSchedulingThreads } from './api';
import BadgePhoto from './BadgePhoto';
import PillarBadge, { PILLAR_DISPLAY } from './Pillar';
import ChatPanel from './ChatPanel';
import { avpFromPath, setPathForAvp } from './navigation';

interface Props {
  idToken: string;
}

const PAGE_SIZE_OPTIONS = [
  { label: '20 items', value: '20' },
  { label: '50 items', value: '50' },
  { label: '100 items', value: '100' },
];

function formatDate(iso: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function SchedulingTable({ idToken }: Props) {
  const [items, setItems] = useState<SchedulingThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedThread, setSelectedThread] = useState<SchedulingThread | null>(null);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tokenHistory, setTokenHistory] = useState<(string | undefined)[]>([undefined]);
  const [hideAssigned, setHideAssigned] = useState(true);
  const [sortingColumn, setSortingColumn] = useState<TableProps.SortingColumn<SchedulingThread> | undefined>(undefined);
  const [sortingDescending, setSortingDescending] = useState(false);
  const [selectedPillars, setSelectedPillars] = useState<MultiselectProps.Option[]>([]);
  const [selectedManagers, setSelectedManagers] = useState<MultiselectProps.Option[]>([]);
  const [pillarOptions, setPillarOptions] = useState<MultiselectProps.Option[]>([]);
  const [managerOptions, setManagerOptions] = useState<MultiselectProps.Option[]>([]);

  const load = useCallback(async (page: number, size: number, searchText: string, tokens: (string | undefined)[], hideAssignedFlag: boolean, sortField?: string, sortDesc?: boolean, pillars?: string, managers?: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await getSchedulingThreads(idToken, {
        pageSize: size,
        nextToken: tokens[page - 1],
        search: searchText || undefined,
        hideAssigned: hideAssignedFlag,
        sortBy: sortField || undefined,
        sortOrder: sortField ? (sortDesc ? 'desc' : 'asc') : undefined,
        filterPillar: pillars || undefined,
        filterManager: managers || undefined,
      });
      setItems(res.threads);
      setTotal(res.total);
      // Update filter options from server response
      if (res.availableFilters) {
        setPillarOptions(
          res.availableFilters.pillars.map((p) => ({ label: PILLAR_DISPLAY[p]?.label || p, value: p }))
        );
        setManagerOptions(
          res.availableFilters.managers.map((m) => ({ label: m, value: m }))
        );
      }
      // Store next token for the next page
      if (res.nextToken && tokens.length <= page) {
        const newTokens = [...tokens];
        newTokens[page] = res.nextToken;
        setTokenHistory(newTokens);
      }
    } catch (e) {
      // Distinguish a failed fetch from a genuinely empty result set.
      setItems([]);
      setTotal(0);
      setError(e instanceof Error ? e.message : 'Failed to load scheduling threads.');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  const pillarFilterValue = selectedPillars.map((o) => o.value).join(',');
  const managerFilterValue = selectedManagers.map((o) => o.value).join(',');

  useEffect(() => {
    setCurrentPage(1);
    setTokenHistory([undefined]);
    void load(1, pageSize, appliedSearch, [undefined], hideAssigned, sortingColumn?.sortingField, sortingDescending, pillarFilterValue, managerFilterValue);
  }, [load, pageSize, appliedSearch, hideAssigned, sortingColumn, sortingDescending, pillarFilterValue, managerFilterValue]);

  // Open a thread and reflect it in the URL so the link is shareable.
  const openThread = useCallback((thread: SchedulingThread | null) => {
    setSelectedThread(thread);
    setPathForAvp(thread ? thread.avpId : null);
  }, []);

  // Resolve an AVP id from the URL to its thread (used for deep links and
  // browser back/forward). Looks it up via the backend search by AVP.
  const openThreadByAvp = useCallback(async (avpId: string) => {
    try {
      const res = await getSchedulingThreads(idToken, { pageSize: 1, search: avpId });
      const thread = res.threads.find((t) => t.avpId.toUpperCase() === avpId.toUpperCase()) || res.threads[0];
      if (thread) {
        setSelectedThread(thread);
      }
    } catch {
      // If resolution fails, fall back to the list without an open thread.
    }
  }, [idToken]);

  // On mount and on browser back/forward, sync the open thread with the URL.
  useEffect(() => {
    const syncFromUrl = () => {
      const avpId = avpFromPath();
      if (avpId) {
        void openThreadByAvp(avpId);
      } else {
        setSelectedThread(null);
      }
    };
    syncFromUrl();
    window.addEventListener('popstate', syncFromUrl);
    return () => window.removeEventListener('popstate', syncFromUrl);
  }, [openThreadByAvp]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    void load(page, pageSize, appliedSearch, tokenHistory, hideAssigned, sortingColumn?.sortingField, sortingDescending, pillarFilterValue, managerFilterValue);
  };

  const handleSearch = () => {
    setAppliedSearch(search);
    setCurrentPage(1);
    setTokenHistory([undefined]);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      {error && (
        <Alert type="error" header="Failed to load scheduling threads" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}
      <Table
        loading={loading}
        loadingText="Loading scheduling threads"
        items={items}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn);
          setSortingDescending(detail.isDescending ?? false);
        }}
        filter={
          <TextFilter
            filteringPlaceholder="Search AVP number (e.g. 23191 or AVP-23191)"
            filteringText={search}
            onChange={({ detail }) => setSearch(detail.filteringText)}
            onDelayedChange={() => handleSearch()}
          />
        }
        header={
          <Header
            counter={`(${total})`}
            actions={
              <SpaceBetween direction="horizontal" size="m">
                <Multiselect
                  selectedOptions={selectedPillars}
                  onChange={({ detail }) => setSelectedPillars([...detail.selectedOptions] as MultiselectProps.Option[])}
                  options={pillarOptions}
                  placeholder="Filter by Pillar"
                  hideTokens
                />
                <Multiselect
                  selectedOptions={selectedManagers}
                  onChange={({ detail }) => setSelectedManagers([...detail.selectedOptions] as MultiselectProps.Option[])}
                  options={managerOptions}
                  placeholder="Filter by Manager"
                  hideTokens
                />
                <Checkbox
                  checked={hideAssigned}
                  onChange={({ detail }) => setHideAssigned(detail.checked)}
                >
                  Hide already scheduled tests
                </Checkbox>
                <Select
                  selectedOption={PAGE_SIZE_OPTIONS.find((o) => o.value === String(pageSize)) || null}
                  options={PAGE_SIZE_OPTIONS}
                  onChange={({ detail }) => {
                    setPageSize(Number(detail.selectedOption.value));
                    setCurrentPage(1);
                    setTokenHistory([undefined]);
                  }}
                />
                <Button iconName="refresh" onClick={() => { setTokenHistory([undefined]); void load(1, pageSize, appliedSearch, [undefined], hideAssigned, sortingColumn?.sortingField, sortingDescending, pillarFilterValue, managerFilterValue); setCurrentPage(1); }} />
              </SpaceBetween>
            }
          >
            Scheduling Threads
          </Header>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={totalPages || 1}
            onChange={({ detail }) => handlePageChange(detail.currentPageIndex)}
          />
        }
        empty="No scheduling threads found"
        columnDefinitions={[
          {
            id: 'avp',
            header: 'AVP',
            cell: (item) => (
              <Link href={`/${item.avpId}`} onFollow={(e) => { e.preventDefault(); openThread(item); }}>
                {item.avpId}
              </Link>
            ),
          },
          {
            id: 'title',
            header: 'Title',
            cell: (item) => item.title,
          },
          {
            id: 'pillar',
            header: 'Pillar',
            sortingField: 'pillar',
            cell: (item) => <PillarBadge pillar={item.pillar} />,
          },
          {
            id: 'manager',
            header: 'Manager',
            sortingField: 'pillarManager',
            cell: (item) =>
              item.pillarManager ? (
                <span className="manager-cell">
                  <BadgePhoto alias={item.pillarManager} size={28} />
                  {item.pillarManager}
                </span>
              ) : (
                '—'
              ),
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item) =>
              item.status === 'assigned' ? (
                <StatusIndicator type="success">Assigned</StatusIndicator>
              ) : item.status === 'closed_ignored' ? (
                <StatusIndicator type="error">Closed/Ignored</StatusIndicator>
              ) : item.status === 'assigned_externally' ? (
                <StatusIndicator type="info">Assigned outside ASPEN</StatusIndicator>
              ) : (
                <StatusIndicator type="in-progress">Discussion ongoing</StatusIndicator>
              ),
          },
          {
            id: 'createdAt',
            header: 'Created at',
            sortingField: 'createdAt',
            cell: (item) => formatDate(item.createdAt),
          },
          {
            id: 'lastMessage',
            header: 'Last message',
            cell: (item) => formatDate(item.lastMessageAt),
          },
        ]}
      />
      {selectedThread && (
        <ChatPanel
          idToken={idToken}
          thread={selectedThread}
          onClose={() => openThread(null)}
          onStatusChange={(newStatus) => {
            setItems((prev) =>
              prev.map((t) =>
                t.threadUuid === selectedThread.threadUuid ? { ...t, status: newStatus } : t,
              ),
            );
            setSelectedThread((prev) => prev ? { ...prev, status: newStatus } : null);
          }}
        />
      )}
    </>
  );
}
